import { useEffect, useRef } from "react";
import type { MouseEvent as ReactMouseEvent, SVGProps } from "react";
import { Link, useLocation } from "react-router-dom";
import { findNavDestinationByPath, MOBILE_MORE_DESTINATIONS } from "./navigation";

/** Same 1.75px-stroke line-icon vocabulary as `navigation.tsx`/`AppShell.tsx`. Decorative. */
function ChevronIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

/** Decorative — the button's own `aria-label="Cerrar"` already carries the meaning. */
function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

/**
 * Mobile "Más" bottom sheet (REDISENO-PANEL.md §3.2, panel-redesign-v3T2):
 * user card (initials, name, role), Plantillas / Estado del sistema (icon +
 * subtitle + chevron), and "Cerrar sesión" (red). Accessible dialog
 * semantics mirror `Modal.tsx`'s own established contract (role="dialog",
 * aria-modal, focus-managed open, Tab trap, Escape closes, focus returns to
 * whatever had focus before it opened — here, the bottom bar's "Más"
 * trigger) rather than duplicating a second, divergent a11y pattern for a
 * bottom-anchored sheet instead of a centered dialog.
 */
export function MobileMoreSheet({
  open,
  onClose,
  onNavigate,
  onLogout,
  accountName,
  roleLabel,
  initials,
  restoreFocusFallback,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (event: ReactMouseEvent<HTMLAnchorElement>) => void;
  onLogout: () => void;
  accountName: string;
  roleLabel?: string;
  initials: string;
  /**
   * Codex finding (round 6, PR #381): the default restoration below always
   * refocuses whatever had focus when the sheet OPENED — the mobile bottom
   * bar's "Más" trigger. `AppShell` closes this sheet the moment the
   * viewport crosses into desktop width, where that trigger (and the whole
   * mobile bottom bar) is hidden by the desktop media query, so refocusing
   * it is a no-op. Called lazily in the close/unmount cleanup (never at open
   * time, so it always reflects the LATEST breakpoint) — same contract as
   * `Modal.tsx`'s own `restoreFocusFallback`. Return a still-usable desktop
   * control to redirect focus there instead, or `null`/`undefined` to keep
   * the normal restoration (still-visible trigger).
   */
  restoreFocusFallback?: () => HTMLElement | null;
}) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const activeDestination = findNavDestinationByPath(location.pathname);
  const restoreFocusFallbackRef = useRef(restoreFocusFallback);
  restoreFocusFallbackRef.current = restoreFocusFallback;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = sheetRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === "Tab" && sheetRef.current) {
        const focusableNow = sheetRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusableNow.length === 0) {
          return;
        }
        const first = focusableNow[0]!;
        const last = focusableNow[focusableNow.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const fallback = restoreFocusFallbackRef.current?.();
      (fallback ?? previouslyFocused)?.focus();
    };
    // Same rationale as Modal.tsx: onClose/onNavigate/onLogout are fresh
    // inline closures on every render — only (re)bind when the sheet opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="v3-more-sheet__backdrop" onClick={onClose}>
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Más opciones"
        className="v3-more-sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="v3-more-sheet__handle" aria-hidden="true" />
        <div className="v3-more-sheet__user">
          <span className="v3-more-sheet__avatar" aria-hidden="true">
            {initials}
          </span>
          <span className="v3-more-sheet__identity">
            <span className="v3-more-sheet__name">{accountName}</span>
            {roleLabel && <span className="v3-more-sheet__role">{roleLabel}</span>}
          </span>
        </div>

        {MOBILE_MORE_DESTINATIONS.map(({ destination, subtitle }) => {
          // Codex finding (panel-redesign-v3 PR #381): `NavLink`'s own
          // default (non-`end`) prefix match still matched an unregistered
          // catch-all depth like `/plantillas/id/extra` — the same path
          // `findNavDestinationByPath` (the shell's own classifier) already
          // rejects as the real 404. A plain `<Link>` with `aria-current`
          // driven off that SAME registered-depth predicate means this item
          // can never claim "current" at the same time the bottom bar's
          // "Más" trigger is already current for the 404.
          const isRegisteredActive = activeDestination?.key === destination.key;
          return (
            <Link
              key={destination.key}
              to={destination.to}
              aria-current={isRegisteredActive ? "page" : undefined}
              className="v3-more-sheet__item v2-u-target-min"
              onClick={onNavigate}
            >
              <span className="v3-more-sheet__item-icon">
                <destination.Icon aria-hidden="true" />
              </span>
              <span className="v3-more-sheet__item-text">
                <span>{destination.label}</span>
                <span className="v3-more-sheet__item-subtitle">{subtitle}</span>
              </span>
              <ChevronIcon aria-hidden="true" className="v3-more-sheet__item-chevron" />
            </Link>
          );
        })}

        <button type="button" className="v3-more-sheet__logout v2-u-target-min" onClick={onLogout}>
          Cerrar sesión
        </button>

        {/* Codex finding, panel-redesign-v3 PR #381: Escape and backdrop-click
            were the only ways to dismiss the sheet — no visible, labeled
            close affordance of its own for mouse/touch users. Focus returns
            to the "Más" trigger via the same effect cleanup as every other
            close path above (Escape, backdrop), regardless of which one
            fired. */}
        <button
          type="button"
          className="v3-more-sheet__close v2-u-target-min"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <CloseIcon aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
